<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { PhPlay } from '@phosphor-icons/vue'
import { useIntersectionObserver, usePointer } from '@vueuse/core'

defineProps<{
    url: string
}>()

const el = ref<HTMLElement | null>(null)
const video = ref<HTMLVideoElement | null>(null)
const load = ref(false)
const hasEnded = ref(false)
const { pointerType } = usePointer()

const { stop } = useIntersectionObserver(el, ([entry]) => {
    if (entry?.isIntersecting && pointerType.value !== 'touch') {
        load.value = true
        stop()
    }
})

function onMouseEnter() {
    if (!video.value) return

    video.value.currentTime = 0
    video.value.play()
}

function onMouseLeave() {
    if (!video.value) return

    video.value.pause()
    hasEnded.value = false
}

onMounted(() => {
    if (!el.value || pointerType.value === 'touch') return

    const groupEl = el.value.closest('.group')

    if (!groupEl) return

    groupEl.addEventListener('mouseenter', onMouseEnter)
    groupEl.addEventListener('mouseleave', onMouseLeave)

    onBeforeUnmount(() => {
        groupEl.removeEventListener('mouseenter', onMouseEnter)
        groupEl.removeEventListener('mouseleave', onMouseLeave)
    })
})
</script>

<template>
    <div
        ref="el"
        class="absolute inset-0"
    >
        <div
            class="absolute inset-0 z-20 duration-500 ease-out group-hover:scale-105 group-hover:opacity-0"
        >
            <slot />
        </div>
        <video
            ref="video"
            v-if="load"
            :src="url"
            type="video/webm"
            playsinline
            muted
            class="size-full object-cover object-center"
            @ended="hasEnded = true"
        />
        <PhPlay
            weight="fill"
            class="pointer-events-none absolute top-1/2 left-1/2 z-25 -translate-1/2 text-white drop-shadow-lg transition-opacity duration-300 group-hover:opacity-0"
            :class="{
                '!opacity-100': hasEnded,
            }"
            size="44"
        />
    </div>
</template>
